import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatCnpj, formatCep, validateCnpj } from '../../utils/masks';
import CustomFieldsSection from '../CustomFieldsSection';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Nomes como aparecem na tela — a mensagem de submit precisa dizer QUAL campo
// está errado. Um campo pode estar fora da vista (outra aba, bloco colapsado,
// conta vinculada), e aí "procure o campo vermelho" não ajuda ninguém.
const FIELD_LABELS = {
  razao_social:        'Razão Social',
  cnpj:                'CNPJ',
  contato_email:       'E-mail do contato',
  origem_lead:         'Origem do Lead',
  previsao_fechamento: 'Previsão de fechamento',
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useLeadForm — estado e lógica de submit do LeadFormModal.
 *
 * @param {Object} opts
 * @param {Object|null}  opts.lead        - Lead em edição, ou null para criação
 * @param {string}       opts.funnelType  - 'new_names' | 'base'
 * @param {Function}     opts.onSave      - Callback recebe formData, deve retornar leadId
 * @param {Array}        opts.contas      - Lista de contas para auto-fill de CNPJ
 * @param {Array}        opts.leads       - Lista de leads para ESN auto-fill por conta
 * @param {boolean}      opts.isOpen      - Controla quando inicializar/resetar o form
 */
export function useLeadForm({ lead, funnelType, onSave, contas = [], leads = [], isOpen, initialConta = null }) {
  const { user } = useAuth();

  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [cepLoading, setCepLoading] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [origins, setOrigins] = useState([]);
  const [glowFields, setGlowFields] = useState({});
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [contaAlertMatriz, setContaAlertMatriz] = useState(null);
  const [contaAlertFiliais, setContaAlertFiliais] = useState(0);
  // ZYN-306: oportunidades ativas já existentes com o mesmo CNPJ (aviso não-bloqueante)
  const [duplicateLeads, setDuplicateLeads] = useState([]);
  // Conta vinculada cujo CNPJ não serve (placeholder `IMP_` da importação de
  // realizados, ou DV inválido). O formulário passa a pedir o CNPJ real num
  // campo visível — antes o erro caía num input que nem era renderizado.
  const [contaCnpjPendente, setContaCnpjPendente] = useState(false);

  // ── Inicialização / reset ao abrir ──────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const ft = funnelType || form.funnel_type;
    api.getLeadOrigins(ft ? { funnel_type: ft } : {})
      .then(setOrigins)
      .catch(() => {});
  }, [isOpen, funnelType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      const d = new Date();
      d.setMonth(d.getMonth() + 1);
      const nextMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const initial = lead
        ? { ...lead, previsao_fechamento: lead.previsao_fechamento?.slice(0, 7), origem_lead: lead.origem_lead || '' }
        : {
            funnel_type: funnelType, valor_saas: 0, valor_adesao: 0, valor_servico: 0,
            previsao_fechamento: nextMonth,
            esn_id: user?.role === 'esn' && user?.esn_id ? user.esn_id : null,
          };
      // ZYN-308: criação disparada a partir de uma conta (ex.: toast pós-criação
      // de conta) — pré-preenche empresa/endereço/vínculo a partir da conta.
      if (!lead && initialConta) {
        const cnpjServe = validateCnpj(initialConta.cnpj || '').valid;
        setContaCnpjPendente(!cnpjServe);
        Object.assign(initial, {
          conta_id:         initialConta.id,
          cnpj:             cnpjServe ? formatCnpj(initialConta.cnpj) : '',
          razao_social:     initialConta.razao_social || initialConta.nome || '',
          nome_fantasia:    initialConta.nome_fantasia || initialConta.nome || '',
          titulo:           initialConta.nome_fantasia || initialConta.nome || initialConta.razao_social || '',
          cidade:           initialConta.cidade || '',
          estado:           initialConta.estado || '',
          endereco_rua:     initialConta.endereco_rua || '',
          endereco_numero:  initialConta.endereco_numero || '',
          endereco_bairro:  initialConta.endereco_bairro || '',
          endereco_cep:     initialConta.endereco_cep || '',
          telefone_empresa: initialConta.telefone_empresa || '',
        });
      }
      setForm(initial);
      setErrors({});
      setGlowFields({});
      setInsightsOpen(false);

      // Auto-fetch CNPJ enrichment para leads existentes sem dados de enriquecimento
      if (lead?.cnpj && !lead?.cnae_principal_codigo) {
        const raw = lead.cnpj.replace(/\D/g, '');
        if (raw.length === 14) fetchCnpj(raw);
      }
    }
  }, [isOpen, lead, funnelType, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Alertas de conta (matriz / filiais) ─────────────────────────────────────
  useEffect(() => {
    if (!form.conta_id) {
      setContaAlertMatriz(null);
      setContaAlertFiliais(0);
      return;
    }

    api.getConta(form.conta_id).then(async (c) => {
      if (c.parent_conta_id) {
        try {
          const matriz = await api.getConta(c.parent_conta_id);
          setContaAlertMatriz({ id: c.parent_conta_id, nome: matriz.nome || matriz.razao_social });
        } catch {
          setContaAlertMatriz({ id: c.parent_conta_id, nome: 'Matriz' });
        }
      } else {
        setContaAlertMatriz(null);
      }
    }).catch(() => setContaAlertMatriz(null));

    api.getContaFiliais(form.conta_id).then((filiais) => {
      setContaAlertFiliais(filiais.length);
    }).catch(() => setContaAlertFiliais(0));
  }, [form.conta_id]);

  // ── Fetches externos ─────────────────────────────────────────────────────────

  async function fetchCep(cepRaw) {
    const digits = cepRaw.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm(f => ({
          ...f,
          endereco_rua: data.logradouro || f.endereco_rua,
          endereco_bairro: data.bairro || f.endereco_bairro,
          cidade: data.localidade || f.cidade,
          estado: data.uf || f.estado,
        }));
      } else {
        setErrors(e => ({ ...e, endereco_cep: 'CEP não encontrado' }));
      }
    } catch {
      // silently ignore network errors
    } finally {
      setCepLoading(false);
    }
  }

  async function fetchCnpj(cnpjRaw) {
    const digits = cnpjRaw.replace(/\D/g, '');
    if (digits.length !== 14) return;
    setCnpjLoading(true);
    setErrors(e => ({ ...e, cnpj: null }));
    try {
      const data = await api.lookupCnpj(digits);
      if (data?.found) {
        setForm(f => {
          const nomeFantasia = f.nome_fantasia || data.nome_fantasia || data.nome || '';
          const razaoSocial  = f.razao_social  || data.nome          || '';
          return {
            ...f,
            razao_social:     razaoSocial,
            nome_fantasia:    nomeFantasia,
            titulo:           f.titulo           || nomeFantasia || razaoSocial,
            endereco_rua:     f.endereco_rua     || data.logradouro || '',
            endereco_numero:  f.endereco_numero  || data.numero     || '',
            endereco_bairro:  f.endereco_bairro  || data.bairro     || '',
            cidade:           f.cidade           || data.municipio  || '',
            estado:           f.estado           || data.uf         || '',
            endereco_cep:     f.endereco_cep     || (data.cep ? formatCep(data.cep) : ''),
            telefone_empresa: f.telefone_empresa || (data.telefone ? formatPhone(data.telefone) : ''),
            cnae_principal_codigo: data.cnae_principal_codigo || '',
            cnae_principal_descricao: data.cnae_principal_descricao || '',
            capital_social:        data.capital_social || 0,
            qsa:                   data.qsa || [],
            situacao_cadastral:    data.situacao_cadastral || '',
          };
        });

        setInsightsOpen(true);

        const fieldsToGlow = ['razao_social', 'nome_fantasia', 'titulo', 'endereco_cep', 'endereco_rua', 'endereco_numero', 'endereco_bairro', 'cidade', 'estado', 'telefone_empresa'];
        const newGlow = {};
        fieldsToGlow.forEach(f => { newGlow[f] = true; });
        setGlowFields(newGlow);
        setTimeout(() => setGlowFields({}), 2000);

      } else {
        setErrors(e => ({ ...e, cnpj: 'CNPJ não encontrado na base da Receita Federal' }));
      }
    } catch (err) {
      setErrors(e => ({
        ...e,
        cnpj: err?.message?.includes('502')
          ? 'Serviço de consulta CNPJ indisponível — tente novamente'
          : 'Erro ao consultar CNPJ',
      }));
    } finally {
      setCnpjLoading(false);
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function handleLeadSelect(existingLead) {
    setForm(f => ({
      ...f,
      cnpj:            existingLead.cnpj            || f.cnpj,
      razao_social:    existingLead.razao_social     || f.razao_social,
      nome_fantasia:   existingLead.nome_fantasia    || f.nome_fantasia,
      titulo:          existingLead.titulo           || f.titulo,
      endereco_cep:    existingLead.endereco_cep     || f.endereco_cep,
      endereco_rua:    existingLead.endereco_rua     || f.endereco_rua,
      endereco_numero: existingLead.endereco_numero  || f.endereco_numero,
      endereco_bairro: existingLead.endereco_bairro  || f.endereco_bairro,
      cidade:          existingLead.cidade           || f.cidade,
      estado:          existingLead.estado           || f.estado,
      telefone_empresa: existingLead.telefone_empresa || f.telefone_empresa,
      contato_nome:    existingLead.contato_nome     || f.contato_nome,
      contato_funcao:  existingLead.contato_funcao   || f.contato_funcao,
      contato_telefone: existingLead.contato_telefone || f.contato_telefone,
      contato_email:   existingLead.contato_email    || f.contato_email,
      esn_id:          existingLead.esn_id           || f.esn_id,
      produto_id:      existingLead.produto_id       || f.produto_id,
    }));
  }

  function handleContaSelect(conta) {
    // Placeholder da importação (`IMP_<hash>`) não é CNPJ: não herdar o valor,
    // senão ele desce para o lead e reprova na validação — do servidor também.
    const cnpjServe = validateCnpj(conta.cnpj || '').valid;
    setContaCnpjPendente(!cnpjServe);
    setForm(f => ({
      ...f,
      razao_social:    conta.razao_social || conta.nome    || f.razao_social,
      nome_fantasia:   conta.nome_fantasia || conta.nome   || f.nome_fantasia,
      cnpj:            cnpjServe ? conta.cnpj              : '',
      cidade:          conta.cidade                       || f.cidade,
      estado:          conta.estado || conta.uf           || f.estado,
      endereco_cep:    conta.endereco_cep                 || f.endereco_cep,
      endereco_rua:    conta.endereco_rua                 || f.endereco_rua,
      endereco_numero: conta.endereco_numero              || f.endereco_numero,
      endereco_bairro: conta.endereco_bairro              || f.endereco_bairro,
      telefone_empresa: conta.telefone_empresa            || f.telefone_empresa,
      conta_id:        conta.id,
      titulo: f.titulo || conta.razao_social || conta.nome,
    }));
  }

  // ZYN-306: busca oportunidades ativas (stage não-terminal) com o mesmo CNPJ.
  // Aviso não-bloqueante — lead novo para conta existente é fluxo normal.
  const TERMINAL = ['Fechado/Ganho', 'Ganho', 'Perdido', 'Postergado'];
  async function checkDuplicateLeads(digits) {
    try {
      const res = await api.getLeads({ search: digits });
      const all = Array.isArray(res) ? res : (res?.data || []);
      const dups = all.filter(l =>
        (l.cnpj || '').replace(/\D/g, '') === digits &&
        !TERMINAL.includes(l.stage) &&
        l.id !== lead?.id
      );
      setDuplicateLeads(dups.slice(0, 3));
    } catch {
      setDuplicateLeads([]);
    }
  }

  function handleChange(field, value) {
    const updated = { ...form, [field]: value };

    if (field === 'cnpj') {
      const formatted = formatCnpj(value);
      updated.cnpj = formatted;
      const digits = formatted.replace(/\D/g, '');
      if (digits.length !== 14) setDuplicateLeads([]);
      if (digits.length === 14) {
        checkDuplicateLeads(digits);
        const match = contas.find(c => (c.cnpj || '').replace(/\D/g, '') === digits);
        if (match) {
          updated.razao_social = updated.razao_social || match.nome;
          updated.nome_fantasia = updated.nome_fantasia || match.nome;
          updated.cidade = updated.cidade || match.cidade;
          updated.estado = updated.estado || match.uf;
          updated.conta_id = match.id;
          if (!updated.titulo) updated.titulo = match.nome;
          updated.cnae_principal_codigo = match.cnae_principal_codigo || '';
          updated.cnae_principal_descricao = match.cnae_principal_descricao || '';
          updated.capital_social = match.capital_social || 0;
          updated.situacao_cadastral = match.situacao_cadastral || '';

          if (!match.cnae_principal_codigo && !match.capital_social) {
            fetchCnpj(formatted);
          }
        } else {
          fetchCnpj(formatted);
        }
      }
    }

    if (field === 'conta_id' && !value) setContaCnpjPendente(false);

    if (field === 'conta_id' && value) {
      const linkedLead = leads.find(l => l.conta_id === value && l.esn_id);
      if (linkedLead && !updated.esn_id) {
        updated.esn_id = linkedLead.esn_id;
      }
    }

    if (field === 'endereco_cep') {
      updated.endereco_cep = formatCep(value);
      setErrors(e => ({ ...e, endereco_cep: null }));
    }

    if (field === 'contato_telefone') {
      updated.contato_telefone = formatPhone(value);
    }

    if (field === 'telefone_empresa') {
      updated.telefone_empresa = formatPhone(value);
    }

    if (field === 'nome_fantasia') {
      if (!form.titulo || form.titulo === form.nome_fantasia || form.titulo === form.razao_social) {
        updated.titulo = value;
      }
    }
    if (field === 'razao_social' && !updated.nome_fantasia) {
      updated.nome_fantasia = value;
      if (!updated.titulo) updated.titulo = value;
    }

    setForm(updated);
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
  }

  function validate() {
    const errs = {};
    if (!form.razao_social?.trim()) errs.razao_social = 'Obrigatório';
    if (!form.cnpj?.trim()) {
      errs.cnpj = 'Obrigatório';
    } else if (!validateCnpj(form.cnpj).valid) {
      errs.cnpj = 'CNPJ inválido';
    }
    if (form.contato_email && !validateEmail(form.contato_email)) {
      errs.contato_email = 'E-mail inválido';
    }
    if ((funnelType || form.funnel_type) === 'new_names' && !form.origem_lead) {
      errs.origem_lead = 'Obrigatório para New Names';
    }
    if (!form.previsao_fechamento) {
      errs.previsao_fechamento = 'Obrigatório';
    } else {
      const now = new Date();
      const [y, m] = form.previsao_fechamento.split('-').map(Number);
      if (!lead && (y < now.getFullYear() || (y === now.getFullYear() && m < now.getMonth() + 1))) {
        errs.previsao_fechamento = 'Não pode ser anterior ao mês atual';
      }
    }
    setErrors(errs);
    return errs;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors(errs => { const { _funnel, _submit, ...rest } = errs; return rest; });

    if (user?.role === 'esn' && user?.funnel_access !== 'both') {
      const formFunnelType = form?.funnel_type || funnelType;
      if (formFunnelType !== user.funnel_access) {
        setErrors(errs => ({ ...errs, _funnel: `Você não tem permissão para criar leads neste funil` }));
        return;
      }
    }

    const invalidos = validate();
    if (Object.keys(invalidos).length > 0) {
      const nomes = Object.keys(invalidos).map(f => FIELD_LABELS[f] || f).join(', ');
      setErrors(errs => ({ ...errs, _submit: `Verifique os campos destacados em vermelho: ${nomes}.` }));
      requestAnimationFrame(() => {
        const el = document.querySelector('[data-lead-form] .border-red-400');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }

    try {
      const leadId = await onSave({
        ...form,
        // Título não preenchido manualmente repete a razão social (fallback para
        // nome fantasia). Sem isso, leads criados a partir da conta ficam sem título.
        titulo: form.titulo?.trim() || form.razao_social || form.nome_fantasia || '',
        previsao_fechamento: form.previsao_fechamento ? `${form.previsao_fechamento}-01` : '',
      });
      if (leadId) await CustomFieldsSection.save(leadId);
    } catch (err) {
      const status = err?.status ? ` (HTTP ${err.status})` : '';
      const detail = err?.body?.error || err?.message || 'Erro desconhecido';
      setErrors(errs => ({ ...errs, _submit: `Falha ao salvar lead${status}: ${detail}` }));
      console.error('[LeadFormModal] onSave falhou:', err);
    }
  }

  // ── Derivados ─────────────────────────────────────────────────────────────────

  function inputClass(field) {
    const isGlowing = glowFields[field];
    return `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-[#00DBFF] transition-all duration-500 ${
      isGlowing
        ? 'border-[#00DBFF] ring-2 ring-[#00DBFF]/20 shadow-[0_0_10px_rgba(0,219,255,0.5),_inset_0_0_5px_rgba(0,219,255,0.2)]'
        : errors[field] ? 'border-red-400' : 'border-gray-300'
    }`;
  }

  const COMPLETUDE_FIELDS = [
    'razao_social','cnpj','contato_nome','contato_telefone','contato_email',
    'esn_id','produto_id','valor_saas','previsao_fechamento',
    'principal_dor','decisor_identificado','orcamento_confirmado','prazo_implantacao',
  ];
  const filledCount = COMPLETUDE_FIELDS.filter(f => {
    const v = form[f];
    return v !== null && v !== undefined && v !== '' && v !== 0 && v !== false;
  }).length;
  const completudePct = Math.round((filledCount / COMPLETUDE_FIELDS.length) * 100);
  const completudeColor = completudePct >= 80 ? '#22c55e' : completudePct >= 50 ? '#f59e0b' : '#ef4444';

  return {
    // Estado
    form,
    setForm,
    errors,
    setErrors,
    cepLoading,
    cnpjLoading,
    origins,
    insightsOpen,
    setInsightsOpen,
    contaAlertMatriz,
    contaAlertFiliais,
    duplicateLeads,
    contaCnpjPendente,
    // Handlers
    handleChange,
    handleLeadSelect,
    handleContaSelect,
    handleSubmit,
    fetchCep,
    fetchCnpj,
    // Derivados
    inputClass,
    filledCount,
    completudePct,
    completudeColor,
    COMPLETUDE_FIELDS,
  };
}
